import { useCallback } from 'react'
import _ from 'lodash'
import { useConceptValidator } from './useConceptValidator'
import { BarcodeType, ConceptStatusValueType, ConceptValueDataType } from '@fusion-advantage/backcompat'
import { isConceptStatus } from '@fusion-advantage/formlib'
import { useConceptStatus } from '../../../redux/selectConceptStatus2'
import { useFormDispatch, useFormSelector } from '../../../redux/formStoreHooks'
import { submitStatusUpdates } from '../../../redux/submitStatusUpdates'
import L1ErrorBlock from '../l1/L1ErrorBlock'
import L3BarcodeScanner from '../l3/L3BarcodeScanner'
import { ComplexElementSize } from '../ComplexElementSize'

interface L4BarcodeScannerProps {
	cid?: string
	type?: BarcodeType
	size?: ComplexElementSize
	buttonOnly?: boolean
	buttonLabel?: string
	onScan?: (value: string) => string
}

const L4BarcodeScanner = ({cid, type = BarcodeType.QR, size = ComplexElementSize.Undefined, buttonOnly = false, buttonLabel, onScan}: L4BarcodeScannerProps) => {
	const platform = useFormSelector(state => state.render.platform)
	const { errorNode } = useConceptValidator({
		tagName: 'L4BarcodeScanner',
		cid,
		requireConcept: true,
		allowDataTypes: [ConceptValueDataType.String],
	})
	const conceptStatus = useConceptStatus(cid)
	const dispatch = useFormDispatch()
	const handleChange = useCallback((value: string) => {
		dispatch(submitStatusUpdates([{
			cid: cid!,
			value,
			valueType: ConceptStatusValueType.Chosen,
		}]))
	}, [dispatch, cid])
	const handleScan = useCallback((value: string) => {
		if(onScan) {
			value = onScan(value)
		}
		handleChange(value)
	}, [onScan, handleChange])
	if (errorNode) {
		return errorNode
	}
	if(conceptStatus && !isConceptStatus(conceptStatus)) {
		// probably passed SuperconceptStatus instead of ConceptStatus
		// which doesn't make sense for this control
		return <L1ErrorBlock content={`[L4BarcodeScanner ${cid}] conceptStatus is incorrect type`} />
	}
	return (
		<L3BarcodeScanner
			platform={platform}
			barcodeType={type}
			size={size}
			buttonOnly={buttonOnly}
			buttonLabel={buttonLabel}
			value={conceptStatus?.value as string ?? undefined}
			onChange={handleChange}
			onScan={handleScan}
		/>
	)
}

const LEAVE_BEHIND_PREFIX = 'URL:https://myfusion.info/'

interface L4LeaveBehindScannerProps {
	cid?: string
	onScan?: (value: string) => string
	buttonOnly?: boolean
}

export const L4LeaveBehindScanner = ({cid, onScan, buttonOnly = false}: L4LeaveBehindScannerProps) => {
	const handleScan = useCallback((value: string) => {
		if (value.startsWith(LEAVE_BEHIND_PREFIX)) {
			value = value.substring(LEAVE_BEHIND_PREFIX.length)
		}
		return onScan ? onScan(value) : value
	}, [onScan])
	return (
		<L4BarcodeScanner
			cid={cid}
			buttonLabel='Scan Card'
			onScan={handleScan}
			type={BarcodeType.QR}
			buttonOnly={buttonOnly}
		/>
	)
}

export default L4BarcodeScanner
